import fetchJsonFromRpgLogsApi from './fetchJsonFromRpgLogsApi';
import { type GameViewModel } from '../../resources/js/typings/viewModels/gameViewModel';
import type SeoViewModel from '../../resources/js/typings/viewModels/seoViewModel';
import { type BuildsSpecPageSlugsViewModel } from './getBuildsSpecPageSlugs';
import { type SelectListItem } from '../../resources/js/typings/viewModels/selectListItemViewModel';
import type SpecsByClassSelectItemViewModel from '../../resources/js/typings/viewModels/specsByClassSelectItemViewModel';
import { type BuildsSectionComponentViewModel } from '../../resources/js/components/BuildsSectionComponent/BuildsSectionComponent';
import getCommonViewModels, {
  type CommonViewModels,
} from './getCommonViewModels';
import { type BuildsAdvancedSettingsViewModel } from '@resources/js/components/BuildsAdvancedSettings/BuildsAdvancedSettings';
import { type BreadcrumbViewModel } from '../components/ArchonHeader/ArchonHeader';
import { type RetailWarcraftTalentTreeBlueprint } from '@resources/js/typings/viewModels/retailWarcraftTalentTreeBuildNode';

export type BuildsSpecPageViewModel = {
  seo: SeoViewModel;
  breadcrumbs: BreadcrumbViewModel[];
  title: string;
  description: string;
  lastUpdatedLabel: string;
  lastUpdated: string | null;
  totalParsesLabel: string;
  totalParses: number;
  imageUrl: string | null;
  classLabel: string;
  specLabel: string;
  specOptions: SpecsByClassSelectItemViewModel[];
  selectedSpec: number;
  zoneTypeLabel: string;
  zoneTypeOptions: SelectListItem<string>[];
  selectedZoneType: string;
  categoryOptions: SelectListItem<string>[];
  sectionOptions: SelectListItem<string>[];
  selectedCategory: string;
  showDifficultySelect: boolean;
  difficultyLabel: string;
  difficultyOptions: SelectListItem<string>[];
  selectedDifficulty: string;
  encounterLabel: string;
  encounterOptions: SelectListItem<string>[];
  selectedEncounter: string;
  affixesLabel: string;
  affixesOptions: SelectListItem<string>[];
  selectedAffixes: string;
  talentTreeBlueprints: Record<string, RetailWarcraftTalentTreeBlueprint>;
  sections: BuildsSectionComponentViewModel[];
  advancedSettings: BuildsAdvancedSettingsViewModel;
  metadataSummaryDescription: string;
  warning: string | null;
  archonTitleBadgeLabel?: string;
  archonTitleBadgeTooltip?: string;
};

async function getBuildsSpecPageViewModels(
  slugs: BuildsSpecPageSlugsViewModel,
  translationKeys: string[],
): Promise<
  CommonViewModels & {
    game: GameViewModel;
    translations: Record<string, string>;
    buildsSpecPage: BuildsSpecPageViewModel | null;
  }
> {
  const [data, commonViewModels] = await Promise.all([
    fetchJsonFromRpgLogsApi({
      query: `
query(
  $gameSlug: String!,
  $specSlug: String!,
  $classSlug: String!,
  $zoneTypeSlug: String!,
  $categorySlug: String!,
  $difficultySlug: String!,
  $encounterSlug: String!,
  $affixesSlug: String,
  $translationKeys: [String!]!
) {
  viewModels {
    game(
      buildsZoneTypeSlug: $zoneTypeSlug
    )
    buildsSpecPage(
      gameSlug: $gameSlug,
      specSlug: $specSlug,
      classSlug: $classSlug,
      zoneTypeSlug: $zoneTypeSlug,
      categorySlug: $categorySlug,
      difficultySlug: $difficultySlug,
      encounterSlug: $encounterSlug,
      affixesSlug: $affixesSlug
    )
    translations(keys: $translationKeys)
  }
}
`,
      variables: {
        ...slugs,
        translationKeys,
      },
      gameSlug: slugs.gameSlug,
    }),
    getCommonViewModels(slugs.gameSlug),
  ]);

  return {
    ...commonViewModels,
    ...data.viewModels,
  };
}

export default getBuildsSpecPageViewModels;
